// src/index.tsx
import { forwardRef, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { jsx, jsxs } from "react/jsx-runtime";
var cx = (...values) => values.filter(Boolean).join(" ");
function AwPanel({ children, className, ...props }) {
  return /* @__PURE__ */ jsx("section", { className: cx("aw-panel", className), ...props, children });
}
function AwCard({ children, className, ...props }) {
  return /* @__PURE__ */ jsx("article", { className: cx("aw-card", className), ...props, children });
}
function AwButton({ className, children, variant = "primary", ...props }) {
  return /* @__PURE__ */ jsx("button", { className: cx("aw-button", `aw-button--${variant}`, className), ...props, children });
}
var AwIconButton = forwardRef(function AwIconButton2({ label, children, className, ...props }, ref) {
  return /* @__PURE__ */ jsx("button", { ref, className: cx("aw-icon-button", className), "aria-label": label, ...props, children });
});
function AwField({ label, id, ...props }) {
  return /* @__PURE__ */ jsxs("label", { className: "aw-field", htmlFor: id, children: [
    /* @__PURE__ */ jsx("span", { children: label }),
    /* @__PURE__ */ jsx("input", { id, ...props })
  ] });
}
function AwTextArea({ label, id, ...props }) {
  return /* @__PURE__ */ jsxs("label", { className: "aw-field", htmlFor: id, children: [
    /* @__PURE__ */ jsx("span", { children: label }),
    /* @__PURE__ */ jsx("textarea", { id, ...props })
  ] });
}
function AwSelect({ label, id, children, ...props }) {
  return /* @__PURE__ */ jsxs("label", { className: "aw-field", htmlFor: id, children: [
    /* @__PURE__ */ jsx("span", { children: label }),
    /* @__PURE__ */ jsx("select", { id, ...props, children })
  ] });
}
function AwCheckbox({ label, id, ...props }) {
  return /* @__PURE__ */ jsxs("label", { className: "aw-checkbox", htmlFor: id, children: [
    /* @__PURE__ */ jsx("input", { id, type: "checkbox", ...props }),
    /* @__PURE__ */ jsx("span", { "aria-hidden": "true" }),
    /* @__PURE__ */ jsx("b", { children: label })
  ] });
}
function AwBadge({ children, className }) {
  return /* @__PURE__ */ jsx("span", { className: cx("aw-badge", className), children });
}
function AwStatusPill({ children, state = "pending" }) {
  return /* @__PURE__ */ jsxs("span", { className: `aw-status aw-status--${state}`, children: [
    /* @__PURE__ */ jsx("i", { "aria-hidden": "true" }),
    children
  ] });
}
var priorityLabels = { critical: "P0 Critical", high: "P1 High", medium: "P2 Medium", low: "P3 Low" };
function AwPriorityPill({ priority }) {
  return /* @__PURE__ */ jsx("span", { className: `aw-priority aw-priority--${priority}`, children: priorityLabels[priority] });
}
function AwEnvironmentDot({ environment }) {
  return environment ? /* @__PURE__ */ jsxs("span", { className: `aw-environment-dot aw-environment-dot--${environment}`, title: environment.replace("_", " "), children: [
    /* @__PURE__ */ jsx("i", { "aria-hidden": "true" }),
    environment.replace("_", " ")
  ] }) : null;
}
function AwReadinessRing({ value, label }) {
  const clamped = Math.max(0, Math.min(100, value));
  return /* @__PURE__ */ jsxs("div", { className: "aw-readiness-ring", style: { "--aw-readiness-value": clamped }, children: [
    /* @__PURE__ */ jsxs("svg", { viewBox: "0 0 100 100", "aria-hidden": "true", children: [
      /* @__PURE__ */ jsx("circle", { className: "aw-readiness-ring__track", cx: "50", cy: "50", r: "42" }),
      /* @__PURE__ */ jsx("circle", { className: "aw-readiness-ring__value", cx: "50", cy: "50", r: "42" })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "aw-readiness-ring__copy", children: [
      /* @__PURE__ */ jsxs("strong", { children: [
        clamped,
        "%"
      ] }),
      label && /* @__PURE__ */ jsx("span", { children: label })
    ] })
  ] });
}
function AwPageHeader({ eyebrow, title, actions, children }) {
  return /* @__PURE__ */ jsxs("header", { className: "aw-page-header", children: [
    /* @__PURE__ */ jsxs("div", { className: "aw-page-header__copy", children: [
      eyebrow && /* @__PURE__ */ jsx("p", { children: eyebrow }),
      /* @__PURE__ */ jsx("h1", { children: title }),
      children && /* @__PURE__ */ jsx("div", { className: "aw-page-header__description", children })
    ] }),
    actions && /* @__PURE__ */ jsx("div", { className: "aw-page-header__actions", children: actions })
  ] });
}
function AwEmptyState({ title, children }) {
  return /* @__PURE__ */ jsxs("div", { className: "aw-empty", children: [
    /* @__PURE__ */ jsx("span", { className: "aw-empty__crest", "aria-hidden": "true", children: /* @__PURE__ */ jsx("i", {}) }),
    /* @__PURE__ */ jsx("h2", { children: title }),
    /* @__PURE__ */ jsx("div", { className: "aw-empty__copy", children })
  ] });
}
function AwLoadingState() {
  return /* @__PURE__ */ jsxs("div", { className: "aw-state", role: "status", children: [
    /* @__PURE__ */ jsx("span", { className: "aw-state__sigil", "aria-hidden": "true" }),
    "Loading command chamber\u2026"
  ] });
}
function AwErrorState({ children }) {
  return /* @__PURE__ */ jsx("div", { className: "aw-state aw-state--error", role: "alert", children });
}
function AwTabs({ children, label }) {
  return /* @__PURE__ */ jsx("div", { className: "aw-tabs", role: "tablist", "aria-label": label, children });
}
function AwDropdown({ children, label }) {
  return /* @__PURE__ */ jsxs("details", { className: "aw-dropdown", children: [
    /* @__PURE__ */ jsx("summary", { children: label }),
    /* @__PURE__ */ jsx("div", { children })
  ] });
}
function AwTable({ children, label }) {
  return /* @__PURE__ */ jsx("div", { className: "aw-table-wrap", children: /* @__PURE__ */ jsx("table", { "aria-label": label, children }) });
}
function AwModal({ open, title, children, onClose }) {
  const closeRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    window.setTimeout(() => closeRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("keydown", onKey);
      previous?.focus();
    };
  }, [open, onClose]);
  if (!open) return null;
  const dialog = /* @__PURE__ */ jsx("div", { className: "aw-modal-backdrop", role: "presentation", onMouseDown: (event) => {
    if (event.target === event.currentTarget) onClose();
  }, children: /* @__PURE__ */ jsxs("section", { className: "aw-modal", role: "dialog", "aria-modal": "true", "aria-labelledby": "aw-modal-title", children: [
    /* @__PURE__ */ jsxs("header", { children: [
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("p", { children: "Command Chamber" }),
        /* @__PURE__ */ jsx("h2", { id: "aw-modal-title", children: title })
      ] }),
      /* @__PURE__ */ jsx(AwIconButton, { ref: closeRef, label: "Close dialog", onClick: onClose, children: "\xD7" })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "aw-modal__body", children })
  ] }) });
  return createPortal(dialog, document.body);
}
function AwLifecycleRail({ stages }) {
  return /* @__PURE__ */ jsx("div", { className: "aw-rail", "aria-label": "Release lifecycle", children: stages.map((stage, index) => /* @__PURE__ */ jsxs("div", { className: `aw-rail-stage aw-rail-stage--${stage.state}`, children: [
    /* @__PURE__ */ jsx("span", { "aria-hidden": "true", children: stage.state === "completed" ? "\u2713" : stage.state === "failed" ? "!" : index + 1 }),
    /* @__PURE__ */ jsx("strong", { children: stage.environment.replace(/_/g, " ") }),
    /* @__PURE__ */ jsx("small", { children: stage.detail ?? stage.state }),
    stage.health && /* @__PURE__ */ jsxs("div", { className: `aw-rail-stage__health aw-rail-stage__health--${stage.health.status}`, children: [
      /* @__PURE__ */ jsx("i", { "aria-hidden": "true" }),
      /* @__PURE__ */ jsxs("span", { children: [
        stage.health.status,
        stage.health.uptimePct != null && ` \xB7 ${stage.health.uptimePct}% uptime`,
        stage.health.incidents7d > 0 && ` \xB7 ${stage.health.incidents7d} incident${stage.health.incidents7d === 1 ? "" : "s"}`
      ] })
    ] }),
    stage.onConfigureHealth && /* @__PURE__ */ jsx("button", { type: "button", className: "aw-rail-stage__configure", onClick: stage.onConfigureHealth, children: "Configure" })
  ] }, stage.environment)) });
}
function AwWarTableRoute({ stages, releaseLabel, targetLabel }) {
  return /* @__PURE__ */ jsxs("div", { className: "aw-war-table-route", children: [
    /* @__PURE__ */ jsx("div", { className: "aw-war-table-route__stages", role: "list", "aria-label": "War table release route", children: stages.map((stage, index) => {
      const percent = stage.percent ?? (stage.state === "completed" ? 100 : stage.state === "active" ? 50 : 0);
      const label = stage.state === "completed" ? "Complete" : stage.state === "failed" ? "Failed" : stage.state === "active" ? "In Progress" : "Upcoming";
      return /* @__PURE__ */ jsxs("div", { role: "listitem", className: cx("aw-war-table-route__stage", `aw-war-table-route__stage--${stage.state}`, index === stages.length - 1 && "is-last"), children: [
        /* @__PURE__ */ jsx("span", { "aria-hidden": "true", children: stage.state === "completed" ? "\u2713" : index + 1 }),
        /* @__PURE__ */ jsx("strong", { children: stage.environment.replace(/_/g, " ") }),
        /* @__PURE__ */ jsxs("small", { children: [
          label,
          stage.state !== "pending" && ` \xB7 ${percent}%`
        ] })
      ] }, stage.environment);
    }) }),
    (releaseLabel || targetLabel) && /* @__PURE__ */ jsxs("footer", { className: "aw-war-table-route__footer", children: [
      releaseLabel && /* @__PURE__ */ jsx("span", { children: releaseLabel }),
      targetLabel && /* @__PURE__ */ jsx("span", { children: targetLabel })
    ] })
  ] });
}
function AwTicketCard({ identifier, title, priority, assignee, codex, children }) {
  return /* @__PURE__ */ jsxs(AwCard, { className: "aw-ticket", children: [
    /* @__PURE__ */ jsxs("header", { children: [
      /* @__PURE__ */ jsx("strong", { children: identifier }),
      codex && /* @__PURE__ */ jsx(AwBadge, { children: "Codex" })
    ] }),
    /* @__PURE__ */ jsx("h3", { children: title }),
    /* @__PURE__ */ jsxs("footer", { children: [
      /* @__PURE__ */ jsx(AwBadge, { children: priority }),
      /* @__PURE__ */ jsx("span", { children: assignee })
    ] }),
    children
  ] });
}
function AwDetailDrawer({ open, title, children, onClose }) {
  const drawer = /* @__PURE__ */ jsxs("aside", { className: cx("aw-drawer", open && "is-open"), "aria-hidden": !open, children: [
    /* @__PURE__ */ jsxs("header", { children: [
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("p", { children: "Operational Detail" }),
        /* @__PURE__ */ jsx("h2", { children: title })
      ] }),
      /* @__PURE__ */ jsx(AwIconButton, { label: "Close details", onClick: onClose, children: "\xD7" })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "aw-drawer__body", children })
  ] });
  return createPortal(drawer, document.body);
}
var globalLinks = [["/home", "Home"], ["/projects", "Projects"], ["/my-tasks", "My Tasks"], ["/notifications", "Notifications"], ["/admin", "Administration"]];
var projectLinks = [["overview", "Overview"], ["tasks", "Tasks"], ["releases", "Releases"], ["people", "People"], ["settings", "Settings"]];
function AwCrest({ label = "AW", imageSrc, size = "md" }) {
  return /* @__PURE__ */ jsx("span", { className: `aw-crest aw-crest--${size}`, "aria-hidden": "true", children: imageSrc ? /* @__PURE__ */ jsx("img", { src: imageSrc, alt: "" }) : /* @__PURE__ */ jsx("b", { children: label.slice(0, 2).toUpperCase() }) });
}
function AwAvatar({ name, imageSrc }) {
  return /* @__PURE__ */ jsx("span", { className: "aw-avatar", children: imageSrc ? /* @__PURE__ */ jsx("img", { src: imageSrc, alt: "" }) : /* @__PURE__ */ jsx("b", { children: name.slice(0, 1).toUpperCase() }) });
}
function csrfCookie() {
  return document.cookie.split("; ").find((value) => value.startsWith("awt_csrf="))?.split("=")[1];
}
async function signOut() {
  const token = csrfCookie();
  await fetch("/api/v1/auth/logout", { method: "POST", credentials: "include", headers: token ? { "x-csrf-token": decodeURIComponent(token) } : {} });
  globalThis.location.assign("/projects");
}
function AwBrand() {
  return /* @__PURE__ */ jsxs("a", { href: "/projects", className: "aw-brand", "aria-label": "Ascension Warlords Tracker home", children: [
    /* @__PURE__ */ jsx(AwCrest, { label: "AW", imageSrc: "/assets/ascension-warlords-crest.webp" }),
    /* @__PURE__ */ jsxs("span", { className: "aw-brand__wordmark", children: [
      /* @__PURE__ */ jsx("strong", { children: "Ascension" }),
      /* @__PURE__ */ jsx("small", { children: "Warlords Tracker" })
    ] })
  ] });
}
function AwGlobalNavigation() {
  const path = globalThis.location?.pathname ?? "";
  return /* @__PURE__ */ jsx("nav", { className: "aw-global-nav", "aria-label": "Global", children: globalLinks.map(([href, label]) => /* @__PURE__ */ jsx("a", { href, "aria-current": path === href || href === "/projects" && path.startsWith("/projects") ? "page" : void 0, children: label }, href)) });
}
function AwUserMenu({ userName }) {
  return /* @__PURE__ */ jsxs("details", { className: "aw-user-menu", children: [
    /* @__PURE__ */ jsxs("summary", { children: [
      /* @__PURE__ */ jsx(AwAvatar, { name: userName ?? "Command", imageSrc: "/assets/profile-command-avatar.webp" }),
      /* @__PURE__ */ jsxs("span", { children: [
        /* @__PURE__ */ jsx("small", { children: "Authenticated Workspace" }),
        /* @__PURE__ */ jsx("strong", { children: userName ?? "Command Chamber" })
      ] }),
      /* @__PURE__ */ jsx("i", { "aria-hidden": "true", children: "\u2304" })
    ] }),
    /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx("a", { href: "/profile", children: "Profile Settings" }),
      /* @__PURE__ */ jsx("button", { type: "button", onClick: () => {
        void signOut();
      }, children: "Sign Out" })
    ] })
  ] });
}
function AwHeader({ userName }) {
  return /* @__PURE__ */ jsxs("header", { className: "aw-header", children: [
    /* @__PURE__ */ jsx(AwBrand, {}),
    /* @__PURE__ */ jsx(AwGlobalNavigation, {}),
    /* @__PURE__ */ jsx(AwUserMenu, { userName })
  ] });
}
function AwSidebarSection({ title, children }) {
  return /* @__PURE__ */ jsxs("section", { className: "aw-sidebar-section", children: [
    /* @__PURE__ */ jsx("h2", { children: title }),
    children
  ] });
}
function AwSidebarItem({ href, label, icon, active }) {
  return /* @__PURE__ */ jsxs("a", { className: "aw-sidebar-item", href, "aria-current": active ? "page" : void 0, children: [
    /* @__PURE__ */ jsx("span", { "aria-hidden": "true", children: icon ?? "\u25C7" }),
    /* @__PURE__ */ jsx("b", { children: label })
  ] });
}
function AwProjectSwitcher({ projectKey }) {
  return /* @__PURE__ */ jsxs("a", { className: "aw-project-switcher", href: "/projects", children: [
    /* @__PURE__ */ jsx(AwCrest, { label: projectKey, size: "sm" }),
    /* @__PURE__ */ jsxs("span", { className: "aw-project-switcher__copy", children: [
      /* @__PURE__ */ jsx("small", { children: "Project" }),
      /* @__PURE__ */ jsx("strong", { children: projectKey })
    ] }),
    /* @__PURE__ */ jsx("i", { "aria-hidden": "true", children: "\u2304" })
  ] });
}
function AwSidebarQuickActions({ children }) {
  return /* @__PURE__ */ jsxs("section", { className: "aw-sidebar-quick-actions", children: [
    /* @__PURE__ */ jsx("h2", { children: "Quick Actions" }),
    /* @__PURE__ */ jsx("div", { children })
  ] });
}
function AwMottoGlyph() {
  return /* @__PURE__ */ jsxs("svg", { className: "aw-motto-glyph", viewBox: "0 0 64 64", "aria-hidden": "true", children: [
    /* @__PURE__ */ jsx("path", { d: "M32 4v34", stroke: "currentColor", strokeWidth: "1.6", fill: "none" }),
    /* @__PURE__ */ jsx("path", { d: "M24 12h16", stroke: "currentColor", strokeWidth: "1.6" }),
    /* @__PURE__ */ jsx("circle", { cx: "32", cy: "42", r: "4.5", stroke: "currentColor", strokeWidth: "1.4", fill: "none" }),
    /* @__PURE__ */ jsx("path", { d: "M14 56c6-6 12-9 18-9s12 3 18 9", stroke: "currentColor", strokeWidth: "1.2", fill: "none" }),
    /* @__PURE__ */ jsx("path", { d: "M10 58c4-3 8-4 12-4M54 58c-4-3-8-4-12-4", stroke: "currentColor", strokeWidth: "1", fill: "none" })
  ] });
}
function AwSidebarMotto() {
  return /* @__PURE__ */ jsxs("div", { className: "aw-sidebar-card aw-sidebar-card--motto", children: [
    /* @__PURE__ */ jsx(AwMottoGlyph, {}),
    /* @__PURE__ */ jsx("strong", { children: "Victory through order" }),
    /* @__PURE__ */ jsx("p", { children: "Glory through unity" })
  ] });
}
function AwSidebar({ mode = "global", projectKey, quickActions }) {
  const path = globalThis.location?.pathname ?? "";
  const active = (href) => path === href || href === "/projects" && path.startsWith("/projects");
  if (mode === "project" && projectKey) {
    return /* @__PURE__ */ jsxs("aside", { className: "aw-sidebar", children: [
      /* @__PURE__ */ jsx(AwProjectSwitcher, { projectKey }),
      /* @__PURE__ */ jsx(AwSidebarSection, { title: "Project", children: projectLinks.map(([route, label]) => /* @__PURE__ */ jsx(AwSidebarItem, { href: `/projects/${projectKey}/${route}`, label, active: path === `/projects/${projectKey}/${route}` || path.startsWith(`/projects/${projectKey}/${route}/`) }, route)) }),
      quickActions && /* @__PURE__ */ jsx(AwSidebarQuickActions, { children: quickActions }),
      /* @__PURE__ */ jsx(AwSidebarMotto, {})
    ] });
  }
  return /* @__PURE__ */ jsxs("aside", { className: "aw-sidebar", children: [
    /* @__PURE__ */ jsxs(AwSidebarSection, { title: "Workspace", children: [
      /* @__PURE__ */ jsx(AwSidebarItem, { href: "/home", label: "Home", active: active("/home") }),
      /* @__PURE__ */ jsx(AwSidebarItem, { href: "/projects", label: "Projects", active: active("/projects") }),
      /* @__PURE__ */ jsx(AwSidebarItem, { href: "/my-tasks", label: "My Tasks", active: active("/my-tasks") }),
      /* @__PURE__ */ jsx(AwSidebarItem, { href: "/notifications", label: "Notifications", active: active("/notifications") })
    ] }),
    /* @__PURE__ */ jsx(AwSidebarSection, { title: "Administration", children: /* @__PURE__ */ jsx(AwSidebarItem, { href: "/admin", label: "Administration", active: active("/admin") }) }),
    /* @__PURE__ */ jsx(AwSidebarMotto, {})
  ] });
}
function AwWorkspace({ children, className }) {
  return /* @__PURE__ */ jsx("section", { className: cx("aw-workspace", className), children });
}
function AwAppShell({ children, userName, projectKey, sidebar = true, quickActions }) {
  return /* @__PURE__ */ jsxs("div", { className: "aw-shell", children: [
    /* @__PURE__ */ jsx("div", { className: "aw-app-background", "aria-hidden": "true" }),
    /* @__PURE__ */ jsx("div", { className: "aw-app-atmosphere", "aria-hidden": "true" }),
    /* @__PURE__ */ jsx(AwHeader, { userName }),
    /* @__PURE__ */ jsx("main", { className: cx("aw-page-stage", sidebar && "aw-page-stage--with-sidebar"), children: /* @__PURE__ */ jsxs("div", { className: "aw-layout", children: [
      sidebar && /* @__PURE__ */ jsx(AwSidebar, { mode: projectKey ? "project" : "global", projectKey, quickActions }),
      /* @__PURE__ */ jsx(AwWorkspace, { children })
    ] }) })
  ] });
}
function AwFilterBar({ children }) {
  return /* @__PURE__ */ jsx("div", { className: "aw-filter-bar", children });
}
function AwPagination({ summary }) {
  return /* @__PURE__ */ jsxs("div", { className: "aw-pagination", children: [
    /* @__PURE__ */ jsx("span", { children: summary }),
    /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx(AwButton, { variant: "secondary", disabled: true, children: "Previous" }),
      /* @__PURE__ */ jsx(AwButton, { variant: "secondary", disabled: true, children: "Next" })
    ] })
  ] });
}
function AwProgress({ value }) {
  const clamped = Math.max(0, Math.min(100, value));
  return /* @__PURE__ */ jsx("div", { className: "aw-progress", "aria-label": `${clamped}% complete`, children: /* @__PURE__ */ jsx("span", { style: { width: `${clamped}%` } }) });
}
function AwMetric({ label, value, detail }) {
  return /* @__PURE__ */ jsxs(AwCard, { className: "aw-metric", children: [
    /* @__PURE__ */ jsx("strong", { children: value }),
    /* @__PURE__ */ jsx("span", { children: label }),
    detail && /* @__PURE__ */ jsx("small", { children: detail })
  ] });
}
function AwProjectCard({ name, projectKey, description, href }) {
  return /* @__PURE__ */ jsx("a", { className: "aw-project-card", href, children: /* @__PURE__ */ jsxs(AwCard, { children: [
    /* @__PURE__ */ jsxs("header", { children: [
      /* @__PURE__ */ jsx(AwCrest, { label: projectKey }),
      /* @__PURE__ */ jsx(AwStatusPill, { state: "pending", children: projectKey })
    ] }),
    /* @__PURE__ */ jsx("h2", { children: name }),
    /* @__PURE__ */ jsx("p", { children: description || "No description recorded." })
  ] }) });
}
function dateValue(value) {
  if (!value) return null;
  const time = (/* @__PURE__ */ new Date(`${value}T00:00:00Z`)).getTime();
  return Number.isFinite(time) ? time : null;
}
function formatTimelineDate(value, scale) {
  return new Intl.DateTimeFormat(void 0, scale === "yearly" ? { year: "numeric" } : { month: "short", year: "numeric" }).format(new Date(value));
}
function timelineDateInput(value) {
  return new Date(value).toISOString().slice(0, 10);
}
function AwRoadmapTimeline({ items, scale = "quarterly", onSelect, onMove }) {
  const today = /* @__PURE__ */ new Date();
  const todayTime = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  const datedItems = items.map((item) => {
    const start = dateValue(item.startDate) ?? dateValue(item.desiredDate) ?? todayTime;
    const end = dateValue(item.desiredDate) ?? start;
    return { ...item, start: Math.min(start, end), end: Math.max(start, end) };
  });
  const values = datedItems.flatMap((item) => [item.start, item.end]).concat(todayTime);
  const rangeStart = Math.min(...values);
  const rangeEnd = Math.max(...values);
  const padding = scale === "monthly" ? 1e3 * 60 * 60 * 24 * 14 : scale === "quarterly" ? 1e3 * 60 * 60 * 24 * 45 : 1e3 * 60 * 60 * 24 * 120;
  const min = rangeStart - padding;
  const max = Math.max(rangeEnd + padding, min + 1e3 * 60 * 60 * 24 * 30);
  const span = max - min;
  const todayLeft = Math.max(0, Math.min(100, (todayTime - min) / span * 100));
  const tickCount = scale === "monthly" ? 6 : scale === "quarterly" ? 5 : 4;
  const ticks = Array.from({ length: tickCount }, (_, index) => min + span * (index / Math.max(1, tickCount - 1)));
  const minWidth = scale === "monthly" ? 900 : scale === "quarterly" ? 1100 : 1320;
  return /* @__PURE__ */ jsxs("div", { className: "aw-roadmap", role: "list", style: { "--aw-roadmap-min-width": `${minWidth}px`, "--aw-roadmap-today": `${todayLeft}%`, "--aw-roadmap-lanes": datedItems.length }, children: [
    ticks.map((tick, index) => /* @__PURE__ */ jsx("span", { className: "aw-roadmap-tick", style: { left: `${index / Math.max(1, tickCount - 1) * 100}%` }, children: formatTimelineDate(tick, scale) }, index)),
    datedItems.map((item, index) => {
      const left = Math.max(0, Math.min(100, (item.start - min) / span * 100));
      const width = Math.max(8, Math.min(100 - left, (item.end - item.start) / span * 100 || 8));
      return /* @__PURE__ */ jsxs("button", { className: `aw-roadmap-item aw-roadmap-item--${item.status.toLowerCase().replace(/\s+/g, "-")}`, style: { left: `${left}%`, top: `calc(54px + ${index} * var(--aw-roadmap-lane-height))`, width: `${width}%` }, type: "button", role: "listitem", draggable: Boolean(onMove), onClick: () => onSelect?.(item), onDragEnd: (event) => {
        if (!onMove) return;
        const container = event.currentTarget.parentElement;
        const bounds = container?.getBoundingClientRect();
        if (!container || !bounds || !event.clientX) return;
        const contentWidth = Math.max(container.scrollWidth, bounds.width);
        const absoluteX = event.clientX - bounds.left + container.scrollLeft;
        const position = Math.max(0, Math.min(1, absoluteX / contentWidth));
        const duration = Math.max(1e3 * 60 * 60 * 24, item.end - item.start);
        const nextStart = min + span * position;
        const nextEnd = Math.min(max, nextStart + duration);
        onMove(item, { startDate: timelineDateInput(nextStart), desiredDate: timelineDateInput(nextEnd) });
      }, children: [
        /* @__PURE__ */ jsx("span", { children: item.projectName ?? "Project" }),
        /* @__PURE__ */ jsx("strong", { children: item.title }),
        /* @__PURE__ */ jsxs("small", { children: [
          item.startDate ?? "Unstarted",
          " \u2192 ",
          item.desiredDate ?? "No target"
        ] }),
        /* @__PURE__ */ jsx(AwStatusPill, { state: item.status === "Completed" ? "completed" : item.status === "At Risk" ? "active" : "pending", children: item.status })
      ] }, item.id);
    })
  ] });
}
function AwReleaseSummary({ version, status, targetDate, progress, href }) {
  return /* @__PURE__ */ jsxs("a", { className: "aw-release-summary", href, children: [
    /* @__PURE__ */ jsx("strong", { children: version }),
    /* @__PURE__ */ jsx(AwStatusPill, { state: status === "Released" ? "completed" : status === "Withdrawn" ? "failed" : "active", children: status }),
    /* @__PURE__ */ jsx(AwProgress, { value: progress }),
    /* @__PURE__ */ jsx("small", { children: targetDate ?? "No target date" })
  ] });
}
function AwTriageActions({ children }) {
  return /* @__PURE__ */ jsxs("aside", { className: "aw-triage-actions", children: [
    /* @__PURE__ */ jsx("h2", { children: "Triage Actions" }),
    children
  ] });
}
var calendarWeekdayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
function AwCalendarGrid({ cells, renderCell }) {
  return /* @__PURE__ */ jsxs("div", { className: "aw-calendar", children: [
    /* @__PURE__ */ jsx("div", { className: "aw-calendar-weekdays", children: calendarWeekdayLabels.map((label) => /* @__PURE__ */ jsx("span", { children: label }, label)) }),
    /* @__PURE__ */ jsx("div", { className: "aw-calendar-grid", children: cells.map((cell) => /* @__PURE__ */ jsxs("div", { className: cx("aw-calendar-cell", !cell.inMonth && "is-outside", cell.isToday && "is-today"), children: [
      /* @__PURE__ */ jsx("span", { className: "aw-calendar-cell__label", children: cell.label }),
      /* @__PURE__ */ jsx("div", { className: "aw-calendar-cell__body", children: renderCell(cell) })
    ] }, cell.date)) })
  ] });
}
export {
  AwAppShell,
  AwAvatar,
  AwBadge,
  AwBrand,
  AwButton,
  AwCalendarGrid,
  AwCard,
  AwCheckbox,
  AwCrest,
  AwDetailDrawer,
  AwDropdown,
  AwEmptyState,
  AwEnvironmentDot,
  AwErrorState,
  AwField,
  AwFilterBar,
  AwGlobalNavigation,
  AwHeader,
  AwIconButton,
  AwLifecycleRail,
  AwLoadingState,
  AwMetric,
  AwModal,
  AwPageHeader,
  AwPagination,
  AwPanel,
  AwPriorityPill,
  AwProgress,
  AwProjectCard,
  AwProjectSwitcher,
  AwReadinessRing,
  AwReleaseSummary,
  AwRoadmapTimeline,
  AwSelect,
  AwSidebar,
  AwSidebarItem,
  AwSidebarMotto,
  AwSidebarQuickActions,
  AwSidebarSection,
  AwStatusPill,
  AwTable,
  AwTabs,
  AwTextArea,
  AwTicketCard,
  AwTriageActions,
  AwUserMenu,
  AwWarTableRoute,
  AwWorkspace
};
